import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { clearSecurityApiKey, customerAuthHeaders } from "../api/client";

export type Customer = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  language: string;
  currency: string;
  favorites: string[];
  created_at?: string;
};

export type Purchase = {
  id: string;
  kind: string;
  label: string;
  status: string;
  amount?: number | string;
  currency?: string;
  company_name?: string;
  created_at?: string;
};

type CustomerContextValue = {
  customer: Customer | null;
  token: string | null;
  loading: boolean;
  language: string;
  currency: string;
  favorites: string[];
  setLanguage: (code: string) => void;
  setCurrency: (code: string) => void;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  register: (payload: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    h_captcha_response?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshPurchases: () => Promise<Purchase[]>;
  updateProfile: (patch: Partial<Pick<Customer, "first_name" | "last_name">>) => Promise<void>;
};

const TOKEN_KEY = "tnk_customer_token";
const LANG_KEY = "tnk_language";
const CURRENCY_KEY = "tnk_currency";
const FAV_KEY = "tnk_favorites";

const CustomerContext = createContext<CustomerContextValue | null>(null);

async function api<T>(
  path: string,
  opts: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body ? { "Content-Type": "application/json" } : {}),
    ...customerAuthHeaders(),
  };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const res = await fetch(path, { ...opts, headers: { ...headers, ...(opts.headers as object) } });
  const data = (await res.json().catch(() => ({}))) as T & {
    error?: string;
    field_errors?: Record<string, string>;
    security_api_key?: string;
  };
  if (!res.ok) {
    const err = new Error(data.error || (res.status === 401 ? "Unauthorized" : "Request failed")) as Error & {
      field_errors?: Record<string, string>;
      status?: number;
    };
    err.field_errors = data.field_errors;
    err.status = res.status;
    throw err;
  }
  return data;
}

function readLocalFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [language, setLanguageState] = useState(
    () => localStorage.getItem(LANG_KEY) || "en",
  );
  const [currency, setCurrencyState] = useState(
    () => localStorage.getItem(CURRENCY_KEY) || "USD",
  );
  const [favorites, setFavorites] = useState<string[]>(() => readLocalFavorites());

  const persistToken = useCallback((next: string | null) => {
    setToken(next);
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const applyCustomer = useCallback((c: Customer | null) => {
    setCustomer(c);
    if (!c) return;
    if (c.language) {
      setLanguageState(c.language);
      localStorage.setItem(LANG_KEY, c.language);
      document.documentElement.lang = c.language;
    }
    if (c.currency) {
      setCurrencyState(c.currency);
      localStorage.setItem(CURRENCY_KEY, c.currency);
    }
    if (Array.isArray(c.favorites)) {
      setFavorites(c.favorites);
      localStorage.setItem(FAV_KEY, JSON.stringify(c.favorites));
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setCustomer(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api<{ customer: Customer }>("/api/customers/me", { token })
      .then((data) => {
        if (!cancelled) applyCustomer(data.customer);
      })
      .catch(() => {
        if (!cancelled) {
          persistToken(null);
          setCustomer(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, applyCustomer, persistToken]);

  const syncPrefs = useCallback(
    async (
      patch: { language?: string; currency?: string; favorites?: string[] },
      authToken?: string | null,
    ) => {
      const t = authToken ?? token;
      if (!t) return;
      try {
        const data = await api<{ customer: Customer }>("/api/customers/me", {
          method: "PATCH",
          token: t,
          body: JSON.stringify(patch),
        });
        applyCustomer(data.customer);
      } catch {
        /* keep local prefs if offline */
      }
    },
    [token, applyCustomer],
  );

  const setLanguage = useCallback(
    (code: string) => {
      setLanguageState(code);
      localStorage.setItem(LANG_KEY, code);
      document.documentElement.lang = code;
      void syncPrefs({ language: code });
    },
    [syncPrefs],
  );

  const setCurrency = useCallback(
    (code: string) => {
      setCurrencyState(code);
      localStorage.setItem(CURRENCY_KEY, code);
      void syncPrefs({ currency: code });
    },
    [syncPrefs],
  );

  const toggleFavorite = useCallback(
    (id: string) => {
      setFavorites((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
        void syncPrefs({ favorites: next });
        return next;
      });
    },
    [syncPrefs],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  const login = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const data = await api<{
        token: string;
        customer: Customer;
        }>("/api/customers/login", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          h_captcha_response: captchaToken || undefined,
        }),
      });
      persistToken(data.token);
      applyCustomer(data.customer);
    },
    [persistToken, applyCustomer],
  );

  const loginWithToken = useCallback(
    async (nextToken: string) => {
      const t = nextToken.trim();
      if (!t) throw new Error("Missing session token.");
      const data = await api<{ customer: Customer; security_api_key?: string }>(
        "/api/customers/me",
        { token: t },
      );
      persistToken(t);
      applyCustomer(data.customer);
    },
    [persistToken, applyCustomer],
  );

  const register = useCallback(
    async (payload: {
      email: string;
      password: string;
      first_name: string;
      last_name: string;
      h_captcha_response?: string;
    }) => {
      const data = await api<{
        token: string;
        customer: Customer;
        }>("/api/customers/register", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      persistToken(data.token);
      applyCustomer(data.customer);
      const localFavs = readLocalFavorites();
      if (
        localFavs.length ||
        localStorage.getItem(LANG_KEY) ||
        localStorage.getItem(CURRENCY_KEY)
      ) {
        await syncPrefs(
          {
            favorites: localFavs.length ? localFavs : undefined,
            language: localStorage.getItem(LANG_KEY) || "en",
            currency: localStorage.getItem(CURRENCY_KEY) || "USD",
          },
          data.token,
        );
      }
    },
    [persistToken, applyCustomer, syncPrefs],
  );

  const logout = useCallback(async () => {
    try {
      if (token) {
        await api("/api/customers/logout", { method: "POST", token });
      }
    } finally {
      persistToken(null);
      clearSecurityApiKey();
      setCustomer(null);
    }
  }, [token, persistToken]);

  const refreshPurchases = useCallback(async () => {
    if (!token) return [];
    const data = await api<{ purchases: Purchase[] }>("/api/customers/me/purchases", { token });
    return data.purchases || [];
  }, [token]);

  const updateProfile = useCallback(
    async (patch: Partial<Pick<Customer, "first_name" | "last_name">>) => {
      if (!token) throw new Error("Please log in.");
      const data = await api<{ customer: Customer }>("/api/customers/me", {
        method: "PATCH",
        token,
        body: JSON.stringify(patch),
      });
      applyCustomer(data.customer);
    },
    [token, applyCustomer],
  );

  const value = useMemo(
    () => ({
      customer,
      token,
      loading,
      language,
      currency,
      favorites,
      setLanguage,
      setCurrency,
      toggleFavorite,
      isFavorite,
      login,
      loginWithToken,
      register,
      logout,
      refreshPurchases,
      updateProfile,
    }),
    [
      customer,
      token,
      loading,
      language,
      currency,
      favorites,
      setLanguage,
      setCurrency,
      toggleFavorite,
      isFavorite,
      login,
      loginWithToken,
      register,
      logout,
      refreshPurchases,
      updateProfile,
    ],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
}
